/**
 * Feet-inches value type with eighths-of-an-inch precision.
 *
 * Internal representation: total eighths of an inch (integer).
 *   1 foot = 96 eighths, 1 inch = 8 eighths.
 *
 * String format: "feet-inches[eighths]"
 *   inches is always 2 digits (00–11).
 *   Optional trailing digit = eighths (1–7); absent or 0 = no fraction.
 *
 * Examples: "27-00" (27'), "0-06" (6"), "0-091" (9 1/8"), "1-022" (1'-2 1/4"), "0-003" (3/8"), "-1-06" (negative)
 *
 * Negative values are supported; the leading `-` applies to the whole magnitude.
 */
export class FtIn {
    _eighths;
    constructor(eighths) {
        this._eighths = Math.round(eighths);
    }
    // --- Parsing ---
    static parse(input) {
        if (!input || !input.includes('-')) {
            throw new Error(`Invalid ft-in format: "${input}". Expected "feet-inches[eighths]".`);
        }
        const isNegative = input.startsWith('-');
        const body = isNegative ? input.slice(1) : input;
        const [feetStr, rest] = body.split('-');
        const feet = parseInt(feetStr, 10);
        if (isNaN(feet) || rest === undefined || rest.length < 2) {
            throw new Error(`Invalid ft-in format: "${input}".`);
        }
        const inches = parseInt(rest.substring(0, 2), 10);
        const eighths = rest.length > 2 ? parseInt(rest[2], 10) : 0;
        if (isNaN(inches) || isNaN(eighths) || inches > 11 || eighths > 7) {
            throw new Error(`Invalid ft-in format: "${input}". Inches must be 0-11, eighths 0-7.`);
        }
        const totalEighths = feet * 96 + inches * 8 + eighths;
        return new FtIn(isNegative ? -totalEighths : totalEighths);
    }
    // --- Factory methods ---
    static fromEighths(eighths) {
        return new FtIn(eighths);
    }
    static fromDecimalFeet(feet) {
        return new FtIn(Math.round(feet * 96));
    }
    static fromDecimalInches(inches) {
        return new FtIn(Math.round(inches * 8));
    }
    static zero() {
        return new FtIn(0);
    }
    // --- Component accessors ---
    get feet() {
        return Math.floor(Math.abs(this._eighths) / 96) * (this._eighths < 0 ? -1 : 1);
    }
    get inches() {
        return Math.floor((Math.abs(this._eighths) % 96) / 8);
    }
    get eighthsRemainder() {
        return Math.abs(this._eighths) % 8;
    }
    get totalEighths() {
        return this._eighths;
    }
    // --- Conversions ---
    toDecimalFeet() {
        return this._eighths / 96;
    }
    toDecimalInches() {
        return this._eighths / 8;
    }
    toString() {
        const sign = this._eighths < 0 ? '-' : '';
        const abs = Math.abs(this._eighths);
        const feet = Math.floor(abs / 96);
        const remaining = abs % 96;
        const inches = Math.floor(remaining / 8);
        const eighths = remaining % 8;
        const inchStr = inches.toString().padStart(2, '0');
        return eighths > 0
            ? `${sign}${feet}-${inchStr}${eighths}`
            : `${sign}${feet}-${inchStr}`;
    }
    // --- Predicates ---
    isZero() {
        return this._eighths === 0;
    }
    // --- Arithmetic ---
    add(other) {
        return new FtIn(this._eighths + other._eighths);
    }
    subtract(other) {
        return new FtIn(this._eighths - other._eighths);
    }
    multiply(factor) {
        return new FtIn(Math.round(this._eighths * factor));
    }
    // --- Comparison ---
    equals(other) {
        return this._eighths === other._eighths;
    }
    compareTo(other) {
        return this._eighths - other._eighths;
    }
    greaterThan(other) {
        return this._eighths > other._eighths;
    }
    lessThan(other) {
        return this._eighths < other._eighths;
    }
    greaterThanOrEqual(other) {
        return this._eighths >= other._eighths;
    }
    lessThanOrEqual(other) {
        return this._eighths <= other._eighths;
    }
}
